<template>
  <v-dialog :value="show" @input="close" persistent width="680" scrollable>
    <v-card>
      <ModalHeader
        :title="headerTitle"
        icon="mdi-clipboard-text"
        :subtitle="headerSubtitle"
        @close="close"
      />
      <v-divider></v-divider>
      <v-card-text class="pa-4" style="max-height: 72vh">
        <div v-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <template v-else-if="lead">
          <!-- Prospect summary -->
          <div class="section-label">Prospect</div>
          <v-row dense class="mb-1">
            <v-col cols="12" sm="6">
              <strong>{{ lead.customer_name }}</strong>
              <div class="caption grey--text">{{ lead.contact_number }}</div>
            </v-col>
            <v-col cols="12" sm="6" class="text-sm-right">
              <v-chip x-small label class="mr-1" v-if="lead.category">{{
                lead.category
              }}</v-chip>
              <v-chip
                x-small
                label
                class="mr-1"
                :color="gradeColor(lead.grade)"
                dark
                v-if="lead.grade"
                >Grade {{ lead.grade }}</v-chip
              >
              <v-chip
                x-small
                label
                :color="lead.converted ? 'green' : 'blue-grey'"
                dark
                >{{ lead.converted ? "Converted" : "Open" }}</v-chip
              >
            </v-col>
          </v-row>

          <v-divider class="my-3"></v-divider>

          <!-- Category-specific detail sheet -->
          <div class="section-label">
            {{ lead.category || "Others" }} details
          </div>
          <v-form ref="form">
            <v-row dense>
              <v-col
                v-for="f in template"
                :key="f.key"
                cols="12"
                :sm="f.cols || 6"
              >
                <v-textarea
                  v-if="f.type === 'textarea'"
                  outlined
                  dense
                  :label="f.label"
                  v-model="details[f.key]"
                  :readonly="readonly"
                  rows="2"
                  auto-grow
                ></v-textarea>
                <v-select
                  v-else-if="f.type === 'select'"
                  outlined
                  dense
                  :label="f.label"
                  v-model="details[f.key]"
                  :items="f.options"
                  :readonly="readonly"
                  clearable
                ></v-select>
                <v-text-field
                  v-else
                  outlined
                  dense
                  :label="f.label"
                  v-model="details[f.key]"
                  :type="
                    f.type === 'number'
                      ? 'number'
                      : f.type === 'date'
                      ? 'date'
                      : 'text'
                  "
                  :prefix="f.suffix === '₱' ? '₱' : undefined"
                  :readonly="readonly"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </template>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="px-4 py-3">
        <v-btn
          v-if="!readonly"
          color="primary"
          depressed
          @click="save"
          :loading="saving"
          >Save Details</v-btn
        >
        <v-btn text @click="close">Close</v-btn>
        <v-spacer></v-spacer>
        <p class="caption grey--text mb-0">leadDetailModal.vue</p>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from "vuex";
import { templateFor } from "@/config/leadCategoryTemplates";

export default {
  props: {
    show: { type: Boolean, default: false },
    leadId: { type: [Number, String], default: null },
    readonly: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      saving: false,
      lead: null,
      details: {},
    };
  },
  computed: {
    template() {
      return templateFor(this.lead && this.lead.category);
    },
    headerTitle() {
      return this.lead ? "Lead #" + this.lead.id + " details" : "Lead details";
    },
    headerSubtitle() {
      return this.lead ? this.lead.category || "Others" : "";
    },
  },
  watch: {
    show(val) {
      if (val && this.leadId) this.load();
    },
  },
  methods: {
    ...mapActions({
      get_lead: "lead/get_lead",
      update_lead: "lead/update_lead",
    }),
    gradeColor(g) {
      return { A: "green", B: "amber darken-2", C: "red" }[g] || "grey";
    },
    async load() {
      this.loading = true;
      this.lead = null;
      this.details = {};
      try {
        const lead = await this.get_lead(this.leadId);
        this.lead = lead;
        this.details = { ...(lead.details || {}) };
      } catch (e) {
        this.$alert({
          title: "Error",
          message: e?.response?.data?.message || "Failed to load lead.",
          color: "error",
          icon: "mdi-alert-circle",
        });
        this.close();
      } finally {
        this.loading = false;
      }
    },
    close() {
      this.$emit("close");
    },
    async save() {
      this.saving = true;
      try {
        // Send the whole lead back (PUT /lead) with the edited detail sheet.
        await this.update_lead({ ...this.lead, details: this.details });
        this.$alert({
          title: "Saved",
          message: "Lead details saved.",
          color: "success",
          icon: "mdi-check-circle",
        });
        this.$emit("changed");
        this.close();
      } catch (e) {
        this.$alert({
          title: "Error",
          message: e?.response?.data?.message || "Failed to save details.",
          color: "error",
          icon: "mdi-alert-circle",
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
